import React, { useState } from 'react';
import { FilterIcon, LayersIcon, MapPinIcon, TagIcon, AlertCircleIcon, CalendarIcon, ServerIcon, WifiIcon, CodeIcon, ShieldIcon, CloudIcon, ChevronDownIcon, XIcon } from 'lucide-react';
interface FilterBarProps {
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  activeCategory: string;
  setActiveCategory: (category: string) => void;
  activeStatus: string;
  setActiveStatus: (status: string) => void;
  activeClassification: string;
  setActiveClassification: (classification: string) => void;
  dateRange: {
    start: string;
    end: string;
  };
  setDateRange: (range: {
    start: string;
    end: string;
  }) => void;
}
export const FilterBar: React.FC<FilterBarProps> = ({
  activeFilter,
  setActiveFilter,
  activeCategory,
  setActiveCategory,
  activeStatus,
  setActiveStatus,
  activeClassification,
  setActiveClassification,
  dateRange,
  setDateRange
}) => {
  const locationFilters = [{
    id: 'all',
    label: 'Semua Lokasi'
  }, {
    id: 'pusat',
    label: 'Pusat/Setuma'
  }, {
    id: 'deputi',
    label: 'Deputi'
  }, {
    id: 'stin',
    label: 'STIN'
  }, {
    id: 'binda',
    label: 'BINDA'
  }, {
    id: 'satker',
    label: 'Satker'
  }];
  const categoryFilters = [{
    id: 'all',
    label: 'Semua Kategori',
    icon: <TagIcon size={14} data-id="element-462" />
  }, {
    id: 'server',
    label: 'Server & Komputasi',
    icon: <ServerIcon size={14} data-id="element-463" />
  }, {
    id: 'network',
    label: 'Jaringan',
    icon: <WifiIcon size={14} data-id="element-464" />
  }, {
    id: 'software',
    label: 'Software',
    icon: <CodeIcon size={14} data-id="element-465" />
  }, {
    id: 'security',
    label: 'Keamanan',
    icon: <ShieldIcon size={14} data-id="element-466" />
  }, {
    id: 'digital',
    label: 'Digital',
    icon: <CloudIcon size={14} data-id="element-467" />
  }];
  const statusFilters = [{
    id: 'all',
    label: 'Semua Status',
    color: 'bg-gray-100 text-gray-700 border-gray-200'
  }, {
    id: 'active',
    label: 'Aktif',
    color: 'bg-green-50 text-green-700 border-green-200'
  }, {
    id: 'maintenance',
    label: 'Perawatan',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200'
  }, {
    id: 'expiring',
    label: 'Akan Habis',
    color: 'bg-orange-50 text-orange-700 border-orange-200'
  }, {
    id: 'critical',
    label: 'Kritis',
    color: 'bg-red-50 text-red-700 border-red-200'
  }];
  const classificationFilters = [{
    id: 'all',
    label: 'Semua',
    color: 'bg-gray-100 text-gray-700'
  }, {
    id: 'sangat-rahasia',
    label: 'Sangat Rahasia',
    color: 'bg-red-100 text-red-800'
  }, {
    id: 'rahasia',
    label: 'Rahasia',
    color: 'bg-orange-100 text-orange-800'
  }, {
    id: 'terbuka',
    label: 'Terbuka',
    color: 'bg-blue-100 text-blue-800'
  }];
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showClassFilter, setShowClassFilter] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    setDateRange({
      ...dateRange,
      [field]: value
    });
  };
  const activeFiltersCount = [activeFilter !== 'all', activeCategory !== 'all', activeStatus !== 'all', activeClassification !== 'all', dateRange.start || dateRange.end].filter(Boolean).length;
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-468">
      <div className="p-4 space-y-4" data-id="element-469">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-470">
          <div className="flex items-center space-x-3" data-id="element-471">
            <div className="p-2 bg-secondary/5 rounded-lg" data-id="element-472">
              <FilterIcon size={18} className="text-secondary" data-id="element-473" />
            </div>
            <div data-id="element-474">
              <h2 className="font-bold text-secondary text-sm" data-id="element-475">Filter Aset</h2>
              <p className="text-xs text-gray-500" data-id="element-476">
                Saring data berdasarkan kriteria
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2" data-id="element-477">
            {activeFiltersCount > 0 && <span className="text-xs bg-primary/10 text-primary font-semibold px-2 py-1 rounded-full" data-id="element-478">
                {activeFiltersCount} filter aktif
              </span>}
            <button className="text-xs text-accent-info hover:underline font-medium" onClick={() => setExpanded(!expanded)} data-id="element-479">
              {expanded ? 'Sembunyikan' : 'Filter Lanjutan'}
            </button>
          </div>
        </div>

        {/* Location Filters */}
        <div data-id="element-480">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-481">
            Lokasi
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-482">
            {locationFilters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${activeFilter === filter.id ? 'bg-secondary text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-483">
                {filter.label}
              </button>)}
          </div>
        </div>

        {/* Category Filters */}
        <div data-id="element-484">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-485">
            Kategori Aset
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-486">
            {categoryFilters.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center transition-all duration-200 ${activeCategory === category.id ? 'bg-primary text-secondary shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-487">
                <span className="mr-1.5" data-id="element-488">{category.icon}</span>
                {category.label}
              </button>)}
          </div>
        </div>

        {/* Quick Filter Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100" data-id="element-489">
          <button className={`flex items-center px-3 py-2 rounded-lg text-xs font-medium transition-all ${showStatusFilter ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => {
          setShowStatusFilter(!showStatusFilter);
          setShowClassFilter(false);
          setShowDateFilter(false);
        }} data-id="element-490">
            <AlertCircleIcon size={14} className="mr-1.5" data-id="element-491" />
            Status
            <ChevronDownIcon size={14} className={`ml-1 transition-transform ${showStatusFilter ? 'rotate-180' : ''}`} data-id="element-492" />
          </button>
          <button className={`flex items-center px-3 py-2 rounded-lg text-xs font-medium transition-all ${showClassFilter ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => {
          setShowClassFilter(!showClassFilter);
          setShowStatusFilter(false);
          setShowDateFilter(false);
        }} data-id="element-493">
            <LayersIcon size={14} className="mr-1.5" data-id="element-494" />
            Klasifikasi
            <ChevronDownIcon size={14} className={`ml-1 transition-transform ${showClassFilter ? 'rotate-180' : ''}`} data-id="element-495" />
          </button>
          <button className={`flex items-center px-3 py-2 rounded-lg text-xs font-medium transition-all ${showDateFilter ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => {
          setShowDateFilter(!showDateFilter);
          setShowStatusFilter(false);
          setShowClassFilter(false);
        }} data-id="element-496">
            <CalendarIcon size={14} className="mr-1.5" data-id="element-497" />
            Tanggal
            <ChevronDownIcon size={14} className={`ml-1 transition-transform ${showDateFilter ? 'rotate-180' : ''}`} data-id="element-498" />
          </button>
        </div>

        {/* Status Filter Panel */}
        {showStatusFilter && <div className="pt-3 border-t border-gray-100 flex flex-wrap gap-2 animate-fadeIn" data-id="element-499">
            {statusFilters.map(status => <button key={status.id} onClick={() => setActiveStatus(status.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${activeStatus === status.id ? status.color + ' ring-2 ring-offset-1 ring-gray-300' : status.color + ' opacity-70 hover:opacity-100'}`} data-id="element-500">
                {status.label}
              </button>)}
          </div>}

        {/* Classification Filter Panel */}
        {showClassFilter && <div className="pt-3 border-t border-gray-100 flex flex-wrap gap-2 animate-fadeIn" data-id="element-501">
            {classificationFilters.map(classification => <button key={classification.id} onClick={() => setActiveClassification(classification.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeClassification === classification.id ? classification.color + ' ring-2 ring-offset-1 ring-gray-300' : classification.color + ' opacity-70 hover:opacity-100'}`} data-id="element-502">
                {classification.label}
              </button>)}
          </div>}

        {/* Date Range Filter Panel */}
        {showDateFilter && <div className="pt-3 border-t border-gray-100 animate-fadeIn" data-id="element-503">
            <div className="grid grid-cols-2 gap-3" data-id="element-504">
              <div data-id="element-505">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-506">
                  Mulai
                </label>
                <input type="date" value={dateRange.start} onChange={e => handleDateRangeChange('start', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm" data-id="element-507" />
              </div>
              <div data-id="element-508">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-509">
                  Akhir
                </label>
                <input type="date" value={dateRange.end} onChange={e => handleDateRangeChange('end', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm" data-id="element-510" />
              </div>
            </div>
          </div>}

        {/* Expanded Filters */}
        {expanded && <div className="pt-4 border-t border-gray-100 space-y-4 animate-fadeIn" data-id="element-511">
            <div data-id="element-512">
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-513">
                Pencarian Cepat
              </label>
              <input type="text" placeholder="Cari berdasarkan nama, ID, atau lokasi..." className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm" data-id="element-514" />
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-515">
              <div data-id="element-516">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-517">
                  Nilai Min
                </label>
                <input type="text" placeholder="Rp 0" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm" data-id="element-518" />
              </div>
              <div data-id="element-519">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-520">
                  Nilai Max
                </label>
                <input type="text" placeholder="Rp 1,000,000,000" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm" data-id="element-521" />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2" data-id="element-522">
              <button className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50" data-id="element-523">
                Reset Filter
              </button>
              <button className="px-4 py-2 bg-secondary text-white rounded-lg text-xs font-medium hover:bg-secondary/90" data-id="element-524">
                Terapkan
              </button>
            </div>
          </div>}
      </div>
    </div>;
};